import React, { useEffect, useState } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
  Box,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  AddCircleOutline as AddIcon,
} from '@mui/icons-material';

export interface SelectionItem {
  id: string;
  primaryText: string | React.ReactNode;
  secondaryText: string | React.ReactNode;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
}

interface PaneProps {
  title: string;
  items: SelectionItem[];
  isLoading?: boolean;
  error?: boolean;
  emptyMessage: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  actionIcon?: React.ReactNode;
  onAction: (item: SelectionItem) => void;
  renderExtras?: (item: SelectionItem) => React.ReactNode;
  isDelete?: boolean;
  readOnly?: boolean;
  pageSize?: number;
}

const PAGE_SIZE = 50;

const SelectionPane: React.FC<PaneProps> = ({
  title,
  items,
  isLoading,
  error,
  emptyMessage,
  searchValue,
  onSearchChange,
  actionIcon,
  onAction,
  renderExtras,
  isDelete,
  readOnly,
  pageSize = PAGE_SIZE,
}) => {
  const [visibleCount, setVisibleCount] = useState(pageSize);

  // Reset visible count when the search filter changes
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [searchValue, pageSize]);

  const visibleItems = items.slice(0, visibleCount);
  const hasMore = items.length > visibleCount;

  return (
    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <TextField
        size="small"
        placeholder="Filter..."
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        fullWidth
      />
      <Box sx={{ flex: 1, overflowY: 'auto', mt: 2, minHeight: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading data.
          </Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {emptyMessage}
          </Typography>
        ) : (
          <List disablePadding>
            {visibleItems.map((item, idx) => (
              <React.Fragment key={item.id}>
                <ListItem alignItems="flex-start" disableGutters sx={{ px: 0 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    flexWrap="wrap"
                    width="100%"
                  >
                    <ListItemText
                      primary={item.primaryText}
                      secondary={item.secondaryText}
                      sx={{ flex: '1 1 180px', minWidth: 0 }}
                    />
                    <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                      {renderExtras && renderExtras(item)}
                      {!readOnly && (isDelete ? (
                        <IconButton
                          edge="end"
                          onClick={() => onAction(item)}
                          color="error"
                          aria-label={`Remove ${typeof item.primaryText === 'string' ? item.primaryText : 'item'} from assignments`}
                        >
                          {actionIcon || <DeleteIcon />}
                        </IconButton>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={actionIcon || <AddIcon fontSize="small" />}
                          onClick={() => onAction(item)}
                          disabled={item.disabled}
                        >
                          Add
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                </ListItem>
                {idx < visibleItems.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
            {hasMore && (
              <ListItem disableGutters sx={{ justifyContent: 'center', py: 1 }}>
                <Button size="small" onClick={() => setVisibleCount((c) => c + pageSize)}>
                  Show more ({items.length - visibleCount} remaining)
                </Button>
              </ListItem>
            )}
          </List>
        )}
      </Box>
    </Paper>
  );
};

interface DualPaneSelectionDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  isDirty?: boolean;

  // Global Status
  isSaving?: boolean;
  saveLabel?: string;
  saveDisabled?: boolean;
  onSave: () => void;
  errorMessage?: string | null;
  onClearError?: () => void;

  // Header Content (e.g. Name Inputs)
  headerContent?: React.ReactNode;

  // Left Pane (Source/Available)
  leftTitle?: string;
  leftItems: SelectionItem[];
  leftSearch: string;
  onLeftSearchChange: (val: string) => void;
  onLeftItemAdd: (item: SelectionItem) => void;
  leftLoading?: boolean;
  leftError?: boolean;

  // Right Pane (Destination/Assigned)
  rightTitle?: string;
  rightCount?: number;
  rightItems: SelectionItem[];
  rightSearch: string;
  onRightSearchChange: (val: string) => void;
  onRightItemRemove: (item: SelectionItem) => void;
  rightLoading?: boolean;
  rightError?: boolean;
  renderRightItemExtras?: (item: SelectionItem) => React.ReactNode;
  extraActions?: React.ReactNode;
  footerInfo?: React.ReactNode;
  readOnly?: boolean;

  // Pagination
  leftPageSize?: number;
  rightPageSize?: number;
}

export const DualPaneSelectionDialog: React.FC<DualPaneSelectionDialogProps> = ({
  open,
  title,
  onClose,
  isSaving,
  saveLabel = 'Save',
  saveDisabled,
  onSave,
  errorMessage,
  onClearError,
  headerContent,

  leftTitle = 'Available',
  leftItems,
  leftSearch,
  onLeftSearchChange,
  onLeftItemAdd,
  leftLoading,
  leftError,

  rightTitle = 'Selected',
  rightCount,
  rightItems,
  rightSearch,
  onRightSearchChange,
  onRightItemRemove,
  rightLoading,
  rightError,
  renderRightItemExtras,
  extraActions,
  footerInfo,
  readOnly,
  isDirty,
  leftPageSize,
  rightPageSize,
}) => {
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const handleClose = () => {
    if (isSaving) return;
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  };

  return (<>
    <Dialog fullScreen open={open} onClose={handleClose}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} disabled={isSaving} aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            {title}
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'background.default',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Custom Header Area */}
        {headerContent && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{headerContent}</Box>
        )}

        {errorMessage && (
          <Alert severity="error" onClose={onClearError}>
            {errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            alignItems: 'flex-start',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Left Pane */}
          <SelectionPane
            title={leftTitle}
            items={leftItems}
            isLoading={leftLoading}
            error={leftError}
            searchValue={leftSearch}
            onSearchChange={onLeftSearchChange}
            onAction={onLeftItemAdd}
            emptyMessage="No items found."
            isDelete={false}
            readOnly={readOnly}
            pageSize={leftPageSize}
          />

          {/* Right Pane */}
          <SelectionPane
            title={`${rightTitle} (${rightCount ?? rightItems.length})`}
            items={rightItems}
            isLoading={rightLoading}
            error={rightError}
            searchValue={rightSearch}
            onSearchChange={onRightSearchChange}
            onAction={onRightItemRemove}
            renderExtras={renderRightItemExtras}
            emptyMessage="No items selected."
            isDelete={true}
            readOnly={readOnly}
            pageSize={rightPageSize}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {extraActions}
        <Box sx={{ flex: 1 }} />
        {footerInfo}
        <Button variant="outlined" onClick={handleClose} disabled={isSaving}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button variant="contained" onClick={onSave} disabled={saveDisabled || isSaving}>
            {isSaving ? 'Saving...' : saveLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <Dialog open={confirmDiscardOpen} onClose={() => setConfirmDiscardOpen(false)}>
      <DialogTitle>Unsaved Changes</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You have unsaved changes. Are you sure you want to close?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDiscardOpen(false)}>Cancel</Button>
        <Button
          onClick={() => {
            setConfirmDiscardOpen(false);
            onClose();
          }}
          color="error"
        >
          Discard
        </Button>
      </DialogActions>
    </Dialog>
  </>);
};
